import axios from "axios";
import { endPoint } from "../config/endPoint";
import { domainWithHttp } from "../config/onboarding/onBoarding";

/*
 * Browser half of the Application Tracker (turbo-config-app). Each job application submit gets
 * a trace id that is sent with the application (`traceId`), so the backend records its own steps
 * under the same id. Once the candidate has been shown the outcome, the browser's own steps —
 * form submitted, response or network error, the message actually shown — are posted in one go.
 * That is what makes a submission that never reached the server visible at all.
 *
 * Fire-and-forget: a tracing failure must never affect the application itself.
 */

export type ApplicationTraceModule = "career" | "microsite";
type StepStatus = "ok" | "failed" | "warning" | "info";

interface TraceEvent {
    step: string;
    status: StepStatus;
    message?: string;
    detail?: Record<string, any>;
    at: number;
}

interface ApplicationDetails {
    email?: string;
    name?: string;
    jobExtId?: string | number;
    jobTitle?: string;
    files?: Record<string, any>;
}

export interface ShownMessage {
    message?: string;
    type: "success" | "error";
}

export interface ApplicationTrace {
    traceId: string;
    /** The application payload is built (documents read) and about to be posted. */
    sent: () => void;
    /** The request came back (or failed to): records the response and the message shown, then reports. */
    finish: (response: any, shown: ShownMessage) => void;
    /** The application could not even be sent (e.g. a document could not be read): records and reports. */
    fail: (step: string, error: any) => void;
}

const baseUrl = process.env.REACT_APP_API_BASE_URL;
// The backend accepts at most 10 events per report.
const MAX_EVENTS = 10;

const newTraceId = (): string => {
    const cryptoApi: any = typeof window !== "undefined" ? (window as any).crypto : undefined;
    if (cryptoApi?.randomUUID) {
        return cryptoApi.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
};

const describeFile = (file: any) => {
    if (!file || typeof file !== "object" || !("name" in file)) {
        return null;
    }
    return {
        name: String(file.name).slice(0, 150),
        sizeKb: typeof file.size === "number" ? Math.round(file.size / 1024) : null,
        type: file.type || null,
    };
};

const text = (value: any, max = 1000): string | undefined => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    const str = typeof value === "string" ? value : JSON.stringify(value);
    return str.slice(0, max);
};

export const startApplicationTrace = (module: ApplicationTraceModule, application: ApplicationDetails): ApplicationTrace => {
    const traceId = newTraceId();
    const events: TraceEvent[] = [];
    let reported = false;

    const step = (name: string, status: StepStatus, message?: string, detail?: Record<string, any>) => {
        if (events.length < MAX_EVENTS) {
            events.push({ step: name, status, message: text(message), detail, at: Date.now() });
        }
    };

    const report = (shown?: ShownMessage) => {
        if (reported || !baseUrl) {
            return;
        }
        reported = true;
        const payload = {
            traceId,
            module,
            domain: domainWithHttp,
            jobExtId: text(application.jobExtId, 64),
            jobTitle: text(application.jobTitle, 255),
            email: text(application.email, 254),
            name: text(application.name, 255),
            candidateMessage: text(shown?.message),
            candidateMessageType: shown?.type,
            events,
        };
        try {
            axios.post(`${baseUrl}${endPoint.applicationTraceEvents}`, payload).catch(() => undefined);
        } catch (_error) {
            // intentionally swallowed
        }
    };

    const files: Record<string, any> = {};
    Object.keys(application.files || {}).forEach((slot) => {
        const described = describeFile(application.files?.[slot]);
        if (described) {
            files[slot] = described;
        }
    });
    step("form_submitted", "ok", "Candidate submitted the application form", {
        files,
        page: typeof window !== "undefined" ? window.location.pathname : null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
    });

    return {
        traceId,
        sent: () => step("request_sent", "ok", "Application sent to the server"),
        finish: (response, shown) => {
            if (reported) {
                return;
            }
            const status = Number(response?.status || 0);
            if (status) {
                const serverMessage = text(response?.data?.message, 500);
                step("response_received", status < 400 ? "ok" : "failed", `HTTP ${status}${serverMessage ? `: ${serverMessage}` : ""}`, {
                    httpStatus: status,
                    statusCode: response?.data?.statusCode ?? null,
                    success: response?.data?.success ?? null,
                });
            } else {
                step("network_error", "failed", "The request got no response from the server (network, timeout or blocked request)", {
                    online: typeof navigator !== "undefined" ? navigator.onLine : null,
                });
            }
            step("message_shown", shown.type === "success" ? "ok" : "failed", shown.message || "(no message was shown)", { type: shown.type });
            report(shown);
        },
        fail: (name, error) => {
            step(name, "failed", text(error?.message || error, 500) || "Unknown error");
            report();
        },
    };
};
